import {
    TOPIC_A2UI_SURFACE,
    TOPIC_LEADER_CONNECTION_STATUS,
    type ConnectionStatus,
} from 'lib/ws/constants'
import type { Service, Hub } from 'lib/ws/election'
import type { A2UIEnvelope } from 'pbts/c1/api/a2ui/v1/a2ui'
import { A2UISurfaceMessage } from 'pbts/c1/api/websocketnotify/v1/websocketnotify'

export type OnA2UISurfaceCallback = (envelope: A2UIEnvelope, conversationId: string) => void

/**
 * A2UIService handles A2UI surface messages through the unified WebSocket.
 * Leader tab broadcasts A2UI messages via hub; all tabs can subscribe.
 */
export class A2UIService implements Service {
    public readonly namespace = 'a2ui'

    // Local tab's subscriptions: conversationId -> callbacks
    private readonly localCallbacks = new Map<string, Set<OnA2UISurfaceCallback>>()

    // Active conversations for this tab
    private readonly activeConversations = new Set<string>()

    private hub: Hub | undefined
    private isLeaderConnected = false

    public async init(hub: Hub): Promise<void> {
        this.hub = hub

        // All tabs: listen for A2UI surface messages from leader
        hub.subscribe(TOPIC_A2UI_SURFACE, (buffer: ArrayBuffer) => {
            try {
                const msg = A2UISurfaceMessage.fromBinary(new Uint8Array(buffer))
                this.handleA2UISurfaceMessage(msg)
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[A2UIService] Failed to deserialize A2UI message from hub', e)
            }
        })

        // Listen for connection status
        hub.subscribe(TOPIC_LEADER_CONNECTION_STATUS, (status: { status: ConnectionStatus }) => {
            const wasConnected = this.isLeaderConnected
            this.isLeaderConnected = status.status === 'connected'

            if (this.isLeaderConnected && !wasConnected) {
                // Connection (re)established - no special handling needed for A2UI
                // since we don't need to re-announce to backend (surfaces are fetched via API)
            }
        })
    }

    /**
     * Subscribe to A2UI surface messages for a conversation.
     */
    public subscribe(conversationId: string, callback: OnA2UISurfaceCallback): void {
        if (!this.localCallbacks.has(conversationId)) {
            this.localCallbacks.set(conversationId, new Set())
        }
        this.localCallbacks.get(conversationId)?.add(callback)
        this.activeConversations.add(conversationId)
    }

    /**
     * Unsubscribe from A2UI surface messages for a conversation.
     */
    public unsubscribe(conversationId: string, callback: OnA2UISurfaceCallback): void {
        const callbacks = this.localCallbacks.get(conversationId)
        if (callbacks) {
            callbacks.delete(callback)
            if (callbacks.size === 0) {
                this.localCallbacks.delete(conversationId)
                this.activeConversations.delete(conversationId)
            }
        }
    }

    /**
     * Called when tab is closing - cleanup subscriptions.
     */
    public onTabClosing(): void {
        this.localCallbacks.clear()
        this.activeConversations.clear()
    }

    // ========== Private Methods ==========

    private handleA2UISurfaceMessage(msg: A2UISurfaceMessage): void {
        const callbacks = this.localCallbacks.get(msg.conversationId)
        if (!callbacks || !msg.envelope) {
            return
        }

        callbacks.forEach((cb) => {
            try {
                if (msg.envelope) {
                    cb(msg.envelope, msg.conversationId)
                }
            } catch {
                // Ignore callback errors
            }
        })
    }

    /**
     * Process a ServerMessage containing an A2UI surface message.
     */
    public static processServerMessage(
        msg: { message: { oneofKind?: string; a2UiSurface?: A2UISurfaceMessage } },
    ): A2UISurfaceMessage | undefined {
        if (msg.message.oneofKind === 'a2UiSurface' && msg.message.a2UiSurface) {
            return msg.message.a2UiSurface
        }
        return undefined
    }
}

// Singleton
let a2uiService: A2UIService | undefined
const serviceReadyCallbacks: Array<(service: A2UIService) => void> = []

export const getA2UIService = (): A2UIService | undefined => a2uiService

/**
 * Register a callback to be notified when A2UI service becomes available.
 * If already available, callback is invoked immediately.
 * Returns an unsubscribe function.
 */
export const onA2UIServiceReady = (callback: (service: A2UIService) => void): (() => void) => {
    if (a2uiService) {
        callback(a2uiService)
    } else {
        serviceReadyCallbacks.push(callback)
    }
    return (): void => {
        const idx = serviceReadyCallbacks.indexOf(callback)
        if (idx >= 0) {
            serviceReadyCallbacks.splice(idx, 1)
        }
    }
}

export const initA2UIService = (): A2UIService => {
    if (!a2uiService) {
        a2uiService = new A2UIService()
        // Notify all waiting callbacks
        for (const cb of serviceReadyCallbacks) {
            try {
                cb(a2uiService)
            } catch {
                // Ignore callback errors
            }
        }
        serviceReadyCallbacks.length = 0
    }
    return a2uiService
}
